import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Progress } from '../ui/progress';
import api from '../../lib/api';
import { CheckCircle2, Circle, Users, FileSpreadsheet, LayoutDashboard, CreditCard, X } from 'lucide-react';
import { Button } from '../ui/button';
import { useNavigate } from 'react-router-dom';

const checklistItems = [
  {
    key: 'crear_cliente',
    label: 'Crear primer cliente',
    description: 'Registra un cliente en el módulo de Rentabilidad',
    icon: Users,
    route: '/rentabilidad'
  },
  {
    key: 'cargar_movimientos',
    label: 'Cargar movimientos financieros',
    description: 'Importa o registra movimientos en el sistema',
    icon: FileSpreadsheet,
    route: '/movimientos'
  },
  {
    key: 'revisar_dashboard',
    label: 'Revisar dashboard',
    description: 'Explora el panel principal de métricas',
    icon: LayoutDashboard,
    route: '/dashboard'
  },
  {
    key: 'registrar_pagos',
    label: 'Registrar pagos pendientes',
    description: 'Crea un pago para dar seguimiento',
    icon: CreditCard,
    route: '/pagos'
  }
];

export const OnboardingChecklist = ({ onDismiss }) => {
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadChecklist();
  }, []);

  const loadChecklist = async () => {
    try {
      const response = await api.get('/usuarios/checklist');
      setChecklist(response.data);
    } catch (error) {
      console.error('Error loading checklist:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleItemClick = (route) => {
    navigate(route);
  };

  if (loading || !checklist) {
    return null;
  }

  // Don't show if all completed
  if (checklist.progress === 100) {
    return null;
  }

  return (
    <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50 shadow-lg">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg text-blue-900 flex items-center gap-2">
              <span className="text-2xl">🚀</span>
              Configuración Inicial
            </CardTitle>
            <p className="text-sm text-blue-700 mt-1">
              Completa estos pasos para aprovechar FinScope al máximo
            </p>
          </div>
          {onDismiss && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onDismiss}
              className="text-blue-600 hover:text-blue-800 hover:bg-blue-100"
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
        
        {/* Progress bar */}
        <div className="mt-4">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-blue-700 font-medium">Progreso</span>
            <span className="text-blue-900 font-bold">{checklist.progress}%</span>
          </div>
          <Progress value={checklist.progress} className="h-2 bg-blue-100" />
        </div>
      </CardHeader>
      
      <CardContent className="pt-0">
        <div className="space-y-2">
          {checklistItems.map((item) => {
            const Icon = item.icon;
            const isCompleted = checklist.checklist[item.key];
            
            return (
              <button
                key={item.key}
                onClick={() => !isCompleted && handleItemClick(item.route)}
                disabled={isCompleted}
                className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all text-left ${
                  isCompleted 
                    ? 'bg-green-50 border border-green-200' 
                    : 'bg-white border border-blue-100 hover:border-blue-300 hover:shadow-md cursor-pointer'
                }`}
              >
                <div className={`p-2 rounded-lg ${
                  isCompleted ? 'bg-green-100' : 'bg-blue-100'
                }`}>
                  <Icon className={`w-4 h-4 ${
                    isCompleted ? 'text-green-600' : 'text-blue-600'
                  }`} />
                </div>
                
                <div className="flex-1 min-w-0">
                  <p className={`font-medium text-sm ${
                    isCompleted ? 'text-green-800 line-through' : 'text-slate-800'
                  }`}>
                    {item.label}
                  </p>
                  <p className={`text-xs truncate ${
                    isCompleted ? 'text-green-600' : 'text-slate-500'
                  }`}>
                    {item.description}
                  </p>
                </div>
                
                {isCompleted ? (
                  <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                ) : (
                  <Circle className="w-5 h-5 text-slate-300 flex-shrink-0" />
                )}
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default OnboardingChecklist;
